import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  User, 
  ArrowRight, 
  TrendingUp,
  DollarSign,
  BarChart3,
  CheckCircle2,
  Search,
  SortAsc,
  SortDesc,
  HelpCircle,
  X,
  Users,
  Shield
} from 'lucide-react';
import { DataIntegrityBadge, LastUpdated, PlatformFooter } from '@/components/common/PageElements';
import { profiles } from '@/data/profileData';

// Define categories for filtering
const ROLE_CATEGORIES = [
  { value: 'all', label: 'All Roles', icon: Users },
  { value: 'head_coach', label: 'Head Coach', icon: User },
  { value: 'assistant', label: 'Technical Staff', icon: User },
  { value: 'player', label: 'Player', icon: User },
];

// Define team filters
const TEAM_FILTERS = [
  { value: 'all', label: 'All Teams' },
  { value: 'usmnt', label: 'USMNT' },
  { value: 'uswnt', label: 'USWNT' },
  { value: 'other', label: 'Other' },
];

const SORT_OPTIONS = [
  { value: 'score', label: 'Brand Score' },
  { value: 'overall', label: 'Overall Index' },
  { value: 'name', label: 'Name' },
  { value: 'team', label: 'Team' },
];

// Professional Profile Photo Component
const ProfilePhotoCard = ({ src, alt, initials, size = 96 }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const containerStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
  };

  return (
    <div 
      className="relative rounded-full overflow-hidden mx-auto"
      style={containerStyle}
    >
      {/* Outer ring and shadow */}
      <div 
        className="absolute inset-0 rounded-full ring-1 ring-white/10"
        style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.1)' }}
      />
      
      {/* Loading skeleton */}
      {!imageLoaded && !imageError && src && (
        <div className="absolute inset-0 bg-muted/50 animate-pulse rounded-full" />
      )}

      {/* Image or placeholder */}
      {!imageError && src ? (
        <img
          src={src}
          alt={alt}
          onError={() => setImageError(true)}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
          width={size}
          height={size}
          className={`w-full h-full object-cover rounded-full transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ) : (
        <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
          {initials ? (
            <span className="font-bold text-slate-300" style={{ fontSize: size * 0.35 }}>
              {initials}
            </span>
          ) : (
            <User className="text-slate-500" style={{ width: size * 0.4, height: size * 0.4 }} />
          )}
        </div>
      )}

      {/* Inner shadow for depth */}
      <div 
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{ boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.15)' }}
      />
    </div>
  );
};

// Athlete Card Component with centered photo
const AthleteCard = ({ profile, profileId }) => {
  const metrics = profile.metrics || {};
  const brandStrength = metrics.brandStrength?.score || 0;
  const overallIndex = metrics.overallIndex?.score || 0;
  const isHeadCoach = profile.role?.toLowerCase().includes('head');
  
  // Get initials for fallback
  const initials = profile.initials || profile.name?.split(' ').map(n => n[0]).join('').slice(0, 2);
  
  return (
    <Card className="bg-card border-border hover:border-primary/30 transition-all duration-300 group h-full flex flex-col" data-testid={`athlete-card-wrapper-${profileId}`}>
      <CardContent className="p-5 flex flex-col h-full">
        {/* Photo Section - Centered at top */}
        <div className="text-center mb-4">
          <ProfilePhotoCard 
            src={profile.photo}
            alt={profile.name}
            initials={initials}
            size={96}
          />
        </div>

        {/* Name and Role */}
        <div className="text-center mb-4">
          <h3 className="text-lg font-bold text-foreground truncate mb-1">{profile.name}</h3>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Badge 
              className={isHeadCoach 
                ? 'bg-primary/10 text-primary border-primary/30' 
                : 'bg-muted text-muted-foreground'
              }
            >
              {profile.role}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-2">{profile.team}</p>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <p className="text-xl font-bold text-primary">{overallIndex}</p>
            <p className="text-xs text-muted-foreground">Overall Index</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30 text-center">
            <p className="text-xl font-bold text-foreground">{brandStrength}</p>
            <p className="text-xs text-muted-foreground">Brand Strength</p>
          </div>
        </div>

        {/* Verified Data Badges */}
        <div className="flex flex-wrap justify-center gap-2 mb-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-stable/10 text-stable">
            <CheckCircle2 className="w-3 h-3" />
            Verified Data
          </span>
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary">
            <TrendingUp className="w-3 h-3" />
            Live Signals
          </span>
        </div>

        {/* CTA Button */}
        <div className="mt-auto">
          <Link to={`/athlete/${profileId}`} data-testid={`athlete-card-${profileId}`}>
            <Button variant="outline" className="w-full group-hover:bg-primary/5 group-hover:border-primary/30">
              Access Detailed Metrics
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// Active Filter Badge Component
const ActiveFilterBadge = ({ label, onRemove }) => (
  <Badge variant="secondary" className="flex items-center gap-1 pr-1">
    {label}
    <button onClick={onRemove} className="ml-1 hover:bg-muted rounded-full p-0.5">
      <X className="w-3 h-3" />
    </button>
  </Badge>
);

export default function AthletesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Convert profiles to array and enrich with metadata
  const athleteProfiles = useMemo(() => {
    return Object.entries(profiles).map(([id, profile]) => {
      const roleLower = profile.role?.toLowerCase() || '';
      const teamLower = profile.team?.toLowerCase() || '';
      
      let roleCategory = 'assistant';
      if (roleLower.includes('head') || roleLower.includes('manager')) {
        roleCategory = 'head_coach';
      } else if (roleLower.includes('player') || roleLower.includes('forward') || 
                 roleLower.includes('midfielder') || roleLower.includes('defender') ||
                 roleLower.includes('goalkeeper')) {
        roleCategory = 'player';
      }
      
      let teamCategory = 'other';
      if (teamLower.includes('men') || teamLower.includes('usmnt')) {
        teamCategory = 'usmnt';
      } else if (teamLower.includes('women') || teamLower.includes('uswnt')) {
        teamCategory = 'uswnt';
      }
      
      return {
        id,
        profile,
        roleCategory,
        teamCategory,
        searchText: `${profile.name} ${profile.role} ${profile.team}`.toLowerCase(),
        brandScore: profile.metrics?.brandStrength?.score || 0,
        overallIndex: profile.metrics?.overallIndex?.score || 0,
      };
    });
  }, []);
  
  // Filter and sort profiles
  const filteredProfiles = useMemo(() => {
    let result = athleteProfiles;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(p => p.searchText.includes(query));
    }
    
    if (roleFilter !== 'all') {
      result = result.filter(p => p.roleCategory === roleFilter);
    }
    
    if (teamFilter !== 'all') {
      result = result.filter(p => p.teamCategory === teamFilter);
    }
    
    result = [...result].sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'score': comparison = b.brandScore - a.brandScore; break;
        case 'overall': comparison = b.overallIndex - a.overallIndex; break;
        case 'name': comparison = a.profile.name.localeCompare(b.profile.name); break;
        case 'team': comparison = (a.profile.team || '').localeCompare(b.profile.team || ''); break;
        default: comparison = 0;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [athleteProfiles, searchQuery, roleFilter, teamFilter, sortBy, sortOrder]);
  
  const toggleSortOrder = () => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');

  const clearAllFilters = () => {
    setSearchQuery('');
    setRoleFilter('all');
    setTeamFilter('all');
  };

  const hasActiveFilters = searchQuery || roleFilter !== 'all' || teamFilter !== 'all';
  const totalProfiles = athleteProfiles.length;

  return (
    <TooltipProvider>
      <div className="min-h-screen flex flex-col bg-gradient-subtle" data-testid="athletes-page">
        <Header />
        
        <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-12">
          {/* Page Header */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Badge className="bg-stable/10 text-stable border-stable/30 border text-xs">Live</Badge>
              </div>
              <div className="flex items-center gap-3">
                <DataIntegrityBadge status="verified" />
                <LastUpdated />
              </div>
            </div>
            
            <div className="flex items-start justify-between gap-4 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight mb-2">Athletes & Coaches</h1>
                  <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
                    Individual brand monetization analysis with verified social metrics, endorsement potential scoring, and market positioning insights for strategic talent management.
                  </p>
                </div>
              </div>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-muted-foreground flex-shrink-0">
                    <HelpCircle className="w-5 h-5" />
                    <span className="hidden sm:inline ml-2">Guide</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs p-4">
                  <p className="text-sm">
                    Use filters to explore coaches and players by brand strength, market position, 
                    and team affiliation. Click "Access Detailed Metrics" for comprehensive analysis.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            
            {/* KPI Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-muted/30 border border-border/50">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{totalProfiles}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Profiles</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-primary">{athleteProfiles.filter(p => p.roleCategory === 'head_coach').length}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Head Coaches</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{athleteProfiles.filter(p => p.roleCategory === 'player').length}</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Players</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-stable">Verified</p>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Data Status</p>
              </div>
            </div>
          </div>
          
          {/* Search and Filters */}
          <div className="mb-8 space-y-4">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, role, or team..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="athlete-search"
                />
                {searchQuery && (
                  <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Role:</span>
                <div className="flex gap-1 flex-wrap">
                  {ROLE_CATEGORIES.map(cat => (
                    <Button key={cat.value} variant={roleFilter === cat.value ? 'default' : 'ghost'} size="sm" onClick={() => setRoleFilter(cat.value)} className="text-xs" data-testid={`role-filter-${cat.value}`}>
                      {cat.label}
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Team:</span>
                <div className="flex gap-1 flex-wrap">
                  {TEAM_FILTERS.map(team => (
                    <Button key={team.value} variant={teamFilter === team.value ? 'default' : 'ghost'} size="sm" onClick={() => setTeamFilter(team.value)} className="text-xs" data-testid={`team-filter-${team.value}`}>
                      {team.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Sort Options */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-border">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">Sort by:</span>
                <div className="flex gap-1 flex-wrap">
                  {SORT_OPTIONS.map(opt => (
                    <Button key={opt.value} variant={sortBy === opt.value ? 'outline' : 'ghost'} size="sm" onClick={() => setSortBy(opt.value)} className={`text-xs ${sortBy === opt.value ? 'border-primary/30 bg-primary/5' : ''}`} data-testid={`sort-${opt.value}`}>
                      {opt.label}
                    </Button>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={toggleSortOrder} className="text-xs" data-testid="sort-order-toggle">
                  {sortOrder === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="text-sm">
                  <span className="font-medium text-foreground">{filteredProfiles.length}</span>
                  <span className="text-muted-foreground"> of {totalProfiles} profiles</span>
                </div>
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs text-muted-foreground hover:text-foreground">
                    Clear all
                  </Button>
                )}
              </div>
            </div>
            
            {hasActiveFilters && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">Active filters:</span>
                {searchQuery && <ActiveFilterBadge label={`Search: "${searchQuery}"`} onRemove={() => setSearchQuery('')} />}
                {roleFilter !== 'all' && <ActiveFilterBadge label={ROLE_CATEGORIES.find(c => c.value === roleFilter)?.label || roleFilter} onRemove={() => setRoleFilter('all')} />}
                {teamFilter !== 'all' && <ActiveFilterBadge label={TEAM_FILTERS.find(t => t.value === teamFilter)?.label || teamFilter} onRemove={() => setTeamFilter('all')} />}
              </div>
            )}
          </div>

          {/* Athletes Grid */}
          {filteredProfiles.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-testid="athletes-grid">
              {filteredProfiles.map(({ id, profile }) => (
                <AthleteCard key={id} profile={profile} profileId={id} />
              ))}
            </div>
          ) : (
            <Card className="bg-muted/20 border-border mb-10">
              <CardContent className="p-8 text-center">
                <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No profiles found</h3>
                <p className="text-muted-foreground mb-4">Try adjusting your search or filter criteria</p>
                <Button variant="outline" onClick={clearAllFilters}>Clear All Filters</Button>
              </CardContent>
            </Card>
          )}

          {/* Info Section */}
          <Card className="bg-muted/20 border-border mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-stable/10 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-stable" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-2">About Individual Analytics</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Our individual brand analysis combines verified social media metrics, media visibility signals, 
                    and market benchmarks to provide actionable monetization insights. Each profile is updated 
                    with verified data from public sources.
                  </p>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />Earnings Potential</span>
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />Growth Projection</span>
                    <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" />Peer Benchmarking</span>
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" />Risk Assessment</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>

        <PlatformFooter />
      </div>
    </TooltipProvider>
  );
}
